/**
 * 보안 설정 「막혔을 요청」 화면 규칙(T-CONTRACT-1031 §3).
 * GET /admin/perm/observations?kind=&q=&cursor=&limit= → { items: [{id, kind, method, route, need[], role, companyType, count, firstSeenAt, lastSeenAt}], next }.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/permObservations.test.mjs`).
 */
import { canMenu, permsOf } from "./perm.js";
import { formatTime } from "./mfa.js";
import { COMPANY_TYPE_LABEL } from "./supportAccess.js";

export const OBSERVATION_HINT = "『기록만』 동안 새 규칙이었다면 막혔을 요청입니다. 『막음』으로 바꾸기 전에 여기를 비워 주세요.";

/**
 * 볼 수 있는 운영자 = 검사 모드를 보는 사람(inapi 가 GET 을 ops.security.manage 로 막음 — 검사 모드 카드와 같은 코드).
 * introspect 를 받기 전엔 아직 모름 → false(저절로 부르는 경로라 403 알림을 띄우지 않게)
 */
export const canSeeObservations = (introspect) =>
  introspect != null && canMenu(permsOf(introspect), "permObservations");

/** 종류(inapi PermObservationKind) → 쉬운 말. 모르는 종류는 코드 그대로 */
export const KIND_LABEL = {
  WOULD_DENY: "권한이 모자람",
  UNMARKED: "경로에 권한 표시가 없음",
  NO_TOKEN: "로그인 정보가 없거나 틀림",
  NOT_STAFF: "고객·웹채팅 로그인으로 직원 기능을 부름",
  SERVICE_TOKEN: "서버끼리 쓰는 로그인으로 부름",
  NO_ACCOUNT: "지운 계정의 로그인",
  RESELLER_ACTING: "리셀러 옛 대행 방식",
  LOOKUP_FAILED: "권한 계산 실패",
  SENDER_WOULD_DENY: "계정에 정한 발신번호 밖",
  OWNER_DELETE: "대표 계정 지우기",
  UI_DIFF: "화면 표시가 옛 규칙과 다름",
  ACCOUNT_SCOPE: "범위 밖 계정을 부름",
  REASON_MISSING: "운영자·리셀러 계정을 만들거나 지울 때 사유 없음",
  CLASS_ROLES: "옛 역할 검사에 걸림",
  MOBILE_TOKEN_NO_ACCOUNT: "모바일·QR 로그인에 계정 없음",
};
export const kindLabel = (kind) => KIND_LABEL[kind] || kind || "-";
export const KIND_OPTIONS = [{ value: "", label: "모든 종류" }, ...Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))];

const COMPANY_LABEL = { CERTILIFE: "서티라이프", ...COMPANY_TYPE_LABEL };

export const PAGE_LIMIT = 50; // 서버 상한 200

/** 경로 앞부분 검색어: 앞뒤 공백을 빼고, 「/」로 시작하지 않으면 붙인다(서버는 앞부분 일치만) */
export const routePrefix = (q) => {
  const t = String(q ?? "").trim();
  if (!t) return "";
  return t.startsWith("/") ? t : `/${t}`;
};

/** 조회 쿼리. 빈 칸은 보내지 않음 */
export const listParams = ({ kind, q, cursor } = {}) => {
  const params = { limit: PAGE_LIMIT };
  if (kind) params.kind = kind;
  const prefix = routePrefix(q);
  if (prefix) params.q = prefix;
  if (cursor) params.cursor = cursor;
  return params;
};

/** 요청 방식(HTTP 메서드) → 쉬운 말. 모르는 방식은 그대로 */
export const METHOD_LABEL = { GET: "보기", POST: "보내기", PUT: "바꾸기", PATCH: "바꾸기", DELETE: "지우기" };
export const methodLabel = (m) => (m ? METHOD_LABEL[String(m).toUpperCase()] || m : "");

/** 이름표에 없는 권한 코드 — 코드는 화면에 그대로 내지 않고 작은 풍선 도움말로만 */
export const UNKNOWN_PERM = "알 수 없는 권한";

/**
 * 한 줄 → 칸들. labels = 모든 앱 권한 이름표 {code: ko}(loadAllPermLabels).
 * need = [{code, ko}] — ko 가 없으면(이름표에 없음) 화면이 UNKNOWN_PERM + 코드 풍선으로 보인다
 */
export const observationRow = (item = {}, labels = {}) => ({
  key: item.id || [item.kind, item.method, item.route].join(" "),
  first: formatTime(item.firstSeenAt),
  last: formatTime(item.lastSeenAt),
  count: Number(item.count) || 0,
  action: methodLabel(item.method),
  route: item.route || "",
  kind: kindLabel(item.kind),
  need: (Array.isArray(item.need) ? item.need : []).map((code) => ({ code, ko: labels[code] || "" })),
  companyType: COMPANY_LABEL[item.companyType] || item.companyType || "-",
});
